import React, {useState, useEffect, useMemo} from 'react';
import './App.css';
import {
    List, ListItemButton, ListItemIcon, ListItemText, IconButton, TextField, Tooltip, Dialog, DialogActions,
    DialogContent, DialogTitle, Button, Box, RadioGroup, FormControlLabel, Radio, Select, MenuItem, InputAdornment, Badge, Popover, CircularProgress
} from '@mui/material';
import { ChatBubbleOutline, Add, Delete, Settings } from '@mui/icons-material';
import { parseChatId } from "../Chat/utils";
import { deleteChat, getChat, updateChat } from "../API/chatAPI";
import { isMobile } from "./utils";
import { ConfirmIconButton } from "../Components/ConfirmIconButton";
import { colorEasyRed } from "./theme";
import {chatHistoryStyles, isCustomChat} from "./ChatsHistoryUtils";
import { decryptKBItemIfEncrypted } from "./cryptoUtils";
import { EmojiPicker } from "../Chat/EmojiPicker";
import Loader from "../Loaders/LocalLoader";
import {renderChatModelsSelect} from "../Chat/ChatSettings";
import {getGroupedModels} from "../Chat/Chat";
import {useConfirm} from "../Components/ConfirmContext";

const {
    listItemTextStyle,
    chatSessionStyle,
    iconStyle,
    buttonStyle,
    tabStyleChat
} = chatHistoryStyles;

const deletedChatIds = {};

export function ChatsList({
                              kbChats, setKBChats, isStreaming, navigateToChat, unreadMessages, onClickChat = () => {},
                              renderSettings, loadMoreChats, hasMoreChats, loadingMore
                          }) {
    const [confirmDeleteChatId, setConfirmDeleteChatId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [blockingLoading, setBlockingLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [chatType, setChatType] = useState('simple');
    const [MyChatData, setMyChatData] = useState(null);
    const [groupedModels, setGroupedModels] = useState(null);

    useEffect(() => {
        if (groupedModels) return;
        setGroupedModels(getGroupedModels())
    }, [groupedModels]);

    useEffect(() => {
        if (!MyChatData?.chatId) return;
        setBlockingLoading(true);
        getChat(MyChatData.chatId).then((data) => {
            setBlockingLoading(false);
            if (!data) return;

            if (data?.title) data.title = decryptKBItemIfEncrypted(data.title);

            if (data?.chatInstructions) {
                data.chatInstructions = decryptKBItemIfEncrypted(data.chatInstructions);
            }

            if (isCustomChat(data)) {
                setChatType('custom');
            } else {
                setChatType('simple');
            }

            setMyChatData(data);
        });
    }, [MyChatData?.chatId]);

    useEffect(() => {
        // This empty useEffect will cause a re-render when unreadMessages changes
    }, [unreadMessages]);

    const handleDeleteKBChat = async (chatId) => {
        setKBChats(prev => prev.filter(app => app.chatId !== chatId));
        if (parseChatId() === chatId) navigateToChat(null);
        deletedChatIds[chatId] = true;
        deleteChat(chatId);
        setConfirmDeleteChatId(null);
    };

    const handleEditChat = async () => {
        setLoading(true);
        await updateChat({
            title: MyChatData?.title,
            chatId: MyChatData?.chatId,
            chatInstructions: chatType === 'custom' ? MyChatData?.chatInstructions : undefined,
            chatIcon: chatType === 'custom' ? MyChatData?.chatIcon : undefined,
            chatModel: chatType === 'custom' ? MyChatData?.chatModel : undefined,
        });
        setLoading(false);
        setKBChats(prev => prev.map(chat => chat.chatId === MyChatData?.chatId ? { ...chat, ...MyChatData } : chat));
        setMyChatData({});
        setDialogOpen(false);
    };

    const handleOpenDialog = (chatId) => {
        setMyChatData({ chatId });
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setMyChatData({});
        setDialogOpen(false);
        setEmojiPickerOpen(false);
    };

    const handleEmojiSelect = (emoji) => {
        setMyChatData(prev => ({ ...prev, chatIcon: emoji.native }));
        setEmojiPickerOpen(false);
        setAnchorEl(null);
    };

    const handleEmojiPickerOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setEmojiPickerOpen(true);
    };

    const handleEmojiPickerClose = () => {
        setAnchorEl(null);
        setEmojiPickerOpen(false);
    };

    const showConfirm = useConfirm();

    return <>
        <Loader open={!!blockingLoading} blocking={true} />
        <ListItemButton
            disableRipple
            selected={!parseChatId() && kbChats?.length}
            disabled={isStreaming}
            onClick={(e) => {
                navigateToChat(null, e);
                onClickChat();
            }}
        >
            <ListItemIcon>
                <Add />
            </ListItemIcon>
            <ListItemText primary={renderSettings?.labelNewChat || 'New Chat'} />
        </ListItemButton>

        <List sx={{ paddingRight: '28px', marginTop: '-6px' }}>
            {kbChats?.filter(o => !deletedChatIds[o.chatId]).map((chat, index) => (
                <ListItemButton
                    disabled={chat.isPlaceholder}
                    selected={chat?.chatId === parseChatId()}
                    disableRipple
                    key={index}
                    onClick={(e) => {
                        if (!confirmDeleteChatId && !MyChatData?.chatId) {
                            onClickChat();
                            navigateToChat(chat?.chatId, e);
                        }
                    }}
                    sx={{
                        ...tabStyleChat,
                        ...chatSessionStyle,
                        '&:hover .iconButtonHover': {
                            opacity: 1,
                        },
                    }}
                >
                    <ListItemIcon>
                        <Badge badgeContent={unreadMessages?.[chat.chatId] || 0}
                               max={999}
                               key={`${chat.chatId}-${unreadMessages?.[chat.chatId] || 0}`}
                               color="error"
                               sx={{
                                   '& .MuiBadge-badge': {
                                       fontSize: '0.68rem',
                                       height: '16px',
                                       minWidth: '12px',
                                       borderRadius: '14px',
                                       animation: unreadMessages?.[chat.chatId] ? 'bubble 1s ease-in-out' : 'none',
                                   },
                               }}
                        >
                            {chat.chatIcon || <ChatBubbleOutline style={{ fontSize: 20 }} />}
                        </Badge>
                    </ListItemIcon>
                    <Tooltip title={chat.title?.length > 25 ? chat.title : undefined}
                             placement="right"
                             PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [0, 60] } }] }}
                    >
                        <ListItemText
                            primary={chat?.title}
                            sx={listItemTextStyle}
                            style={{ marginLeft: isMobile ? -24 * 0 : -26 * 0 }}
                        />
                    </Tooltip>
                    {confirmDeleteChatId === chat.chatId ? (
                        <>
                            <ConfirmIconButton {...{ fontSize: 14, title: 'Cancel', onClick: () => setConfirmDeleteChatId(null) }} />
                            <ConfirmIconButton {...{ fontSize: 14, title: 'Delete', onClick: async () => {
                                    if (isCustomChat(chat) && (chat?.chatInstructions)) {
                                        const confirmed = await showConfirm({
                                            title: 'Delete Custom Chat Confirmation',
                                            content: `Are you sure you want to delete? \n\n "${chat.chatIcon} ${chat.title}"\n\nThis action cannot be undone and all settings will be permanently deleted!`
                                        })

                                        if (!confirmed) return setConfirmDeleteChatId(null);
                                    }

                                    handleDeleteKBChat(chat.chatId);
                                } }} color={colorEasyRed} />
                        </>
                    ) : (
                        <>
                            <IconButton
                                className="iconButtonHover"
                                sx={{
                                    opacity: isMobile ? 1 : 0,
                                    transition: 'opacity 0.3s',
                                    ...buttonStyle,
                                }}
                                style={buttonStyle}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDialog(chat.chatId);
                                }} size="small">
                                <Settings fontSize="small" style={iconStyle} />
                            </IconButton>
                            <IconButton
                                data-testid={"delete-chat-id"}
                                className="iconButtonHover"
                                sx={{
                                    opacity: isMobile ? 1 : 0,
                                    transition: 'opacity 0.3s',
                                    ...buttonStyle,
                                }}
                                style={buttonStyle}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmDeleteChatId(chat.chatId);
                                }} size="small" >
                                <Delete fontSize="small" style={iconStyle} />
                            </IconButton>
                        </>
                    )}
                </ListItemButton>
            ))}
        </List>
        {hasMoreChats && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, px: 3 }}>
                <Button 
                    onClick={loadMoreChats}
                    disabled={loadingMore}
                    variant="text"
                    size="small"
                    sx={{ textTransform: 'none' }}
                >
                    {loadingMore ? <CircularProgress size={20} /> : 'Load more'}
                </Button>
            </Box>
        )}

        {!blockingLoading && <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm" PaperProps={{ style: { minHeight: '520px' } }}>
            <DialogTitle>Edit Chat</DialogTitle>
            <DialogContent>
                <TextField
                    label="Chat Title"
                    value={MyChatData?.title || ''}
                    onChange={(e) => setMyChatData(prev => ({ ...prev, title: e.target.value }))}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    InputProps={{
                        startAdornment: chatType === 'custom' && (
                            <InputAdornment position="start">
                                <Box
                                    sx={{ cursor: 'pointer', marginRight: '8px' }}
                                    onClick={handleEmojiPickerOpen}
                                >
                                    {MyChatData?.chatIcon}
                                </Box>
                            </InputAdornment>
                        ),
                    }}
                />
                <RadioGroup
                    value={chatType}
                    onChange={(e) => {
                        setChatType(e.target.value);
                        if (e.target.value === 'custom') {
                            setMyChatData(prev => ({ ...prev, chatIcon: '🤖' }));
                        }
                    }}
                    row
                >
                    <FormControlLabel value="simple" control={<Radio />} label="Simple" disabled={isCustomChat(MyChatData)} />
                    <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                </RadioGroup>
                {chatType === 'custom' && (
                    <>
                        <Box mt={2} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Popover
                                open={emojiPickerOpen}
                                anchorEl={anchorEl}
                                onClose={handleEmojiPickerClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <EmojiPicker onEmojiSelect={handleEmojiSelect} />
                            </Popover>
                        </Box>
                        <Box mt={2} sx={{ marginTop: '16px', width: '100%', marginBottom: '16px' }}>
                            <TextField
                                label="Chat Instructions"
                                value={MyChatData?.chatInstructions || ''}
                                onChange={(e) => setMyChatData(prev => ({ ...prev, chatInstructions: e.target.value }))}
                                fullWidth
                                multiline
                                minRows={4}
                                inputProps={{ style: { maxHeight: '300px', overflow: 'auto' } }}
                                variant="outlined"
                            />
                        </Box>
                        <Box mt={2}>
                            {renderChatModelsSelect({
                                withHeaders: false,
                                groupedModels,
                                noneOption: 'None',
                                onChange: (model) => {
                                    setMyChatData(prev => ({...prev, chatModel: model}))
                                },
                                model: MyChatData?.chatModel,
                            })}
                        </Box>
                    </>
                )}

            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleEditChat} color="primary" disabled={loading}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>}
    </>
}